import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import logo from '../../assets/images/new-logo.svg';
import globeIcon from '../../assets/icons/globe.svg';
import enFlag from '../../assets/flags/en.png';
import deFlag from '../../assets/flags/de.png';
import frFlag from '../../assets/flags/fr.png';
import spFlag from "../../assets/flags/sp.png"
import nlFlag from '../../assets/flags/nl.svg';
import menuIcon from '../../assets/icons/menu2.svg';
import fullScreenOn from '../../assets/icons/full-screen-on.svg';
import fullScreenOff from '../../assets/icons/full-screen-off.svg';
import {
  logout,
  selectUserLocation,
  selectUserRole,
  selectUserPublicDesignationId,
  IPerson,
  selectPersonLoggedIn,
} from '../../store/slices/auth';
import { authorizeClient } from '../../networking/client';
import { useAppSelector } from '../../store/store';
import arrowDown from '../../assets/icons/arrow-down.svg';
import Avatar from '../UI/Avatar/Avatar';
import { DropDown } from '../UI/DropDown';
import {
  ADMIN_USER_ROLE,
  DOCTOR_USER_ROLE,
  NURSE_USER_ROLE,
} from '../../constants/userRolesConstants';
import classes from './Header.module.scss';
import NotificationDropDown from './NotificationDropDown/NotificationDropDown';
import { useTranslation } from '../../hooks/useTranslations';

interface HeaderProps {
  loggedIn: boolean;
  expandedSidebar?: boolean;
  setShowSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ loggedIn, expandedSidebar, setShowSidebar }) => {
  const { translations, setAppLanguage } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectingLanguage, setSelectingLanguage] = useState<boolean>(false);
  const [openUserDDMenu, setOpenUserDDMenu] = useState(false);
  const [language, setLanguage] = useState<string>(localStorage.getItem('lang') ?? 'en');
  const userLocation: string = useAppSelector<string>(selectUserLocation);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const userPublicDesignationId = useAppSelector<number | undefined>(selectUserPublicDesignationId);
  const [isFullScreen, setIsFullScreen] = React.useState<Element | null>(
    document.fullscreenElement
  );
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);

  const changeLanguage = async (lang: string) => {
    await authorizeClient.put(`/ident/self/settings`, {
      language: lang,
    });
    setSelectingLanguage(false);
    setLanguage(lang);
    setAppLanguage(lang);
  };

  const handleLogout = async () => {
    try {
      await authorizeClient.get('/ident/oauth/logout');
      dispatch(logout());
      navigate('/web');
    } catch (_) { }
  };

  const onToggleLanguageSelect = () => {
    setSelectingLanguage(!selectingLanguage);
  };

  const userRoleLabel = useMemo(() => {
    if (userPublicDesignationId) {
      switch (userPublicDesignationId) {
        case 1:
          return translations('admin');
        case 2:
          return translations('medical.staff');
        case 3:
          return translations('doctor');
        case 4:
          return translations('nutritionist');
        case 7:
          return translations('sales.operator');
        case 8:
          return translations('consultation.operator');
        case 9:
          return translations('check.in.operator');
        case 10:
          return translations('nurse');
        case 11:
          return translations('care.center.agent');
        case 20:
          return translations('diagnostic.operator');
      }
    }
    switch (userRole) {
      case ADMIN_USER_ROLE:
        return translations('admin');
      case DOCTOR_USER_ROLE:
        return translations('doctor');
      case NURSE_USER_ROLE:
        return translations('nurse');
      default:
        return translations('admin');
    }
  }, [userPublicDesignationId, userRole, translations]);

  const personInitials = (): string => {
    if (personLoggedIn.person_firstname?.length > 0 && personLoggedIn.person_lastname?.length > 0)
      return `${personLoggedIn.person_firstname[0]}${personLoggedIn.person_lastname[0]}`;
    return '';
  };

  return (
    <div className={classes.container}>
      <div className={classes.leftContainer}>
        <div className={classes.sidebarToggle} onClick={setShowSidebar}>
          <img alt={'menu icon'} className={classes.sidebarToggleIcon} src={menuIcon} />
        </div>
        {!expandedSidebar && <img className={classes.logo} src={logo} alt="ecocare" />}
      </div>
      <div className={classes.midContainer} />
      <div className={classes.rightContainer}>
        <DropDown
          onClose={onToggleLanguageSelect}
          isOpen={selectingLanguage}
          Toggle={() => (
            <div className={classes.languageContainer} onClick={onToggleLanguageSelect}>
              <img className={classes.languageIcon} src={globeIcon} alt={'globe'} />
              <div className={classes.languageText}>{language.toUpperCase()}</div>
            </div>
          )}
        >
          <div className={`${classes.languagesMenu} ${!selectingLanguage ? classes.hidden : ''}`}>
            <div className={classes.languageMenuItem} onClick={() => changeLanguage('en')}>
              <img className={classes.languageMenuFlag} src={enFlag} alt="enFlag" />
              <div className={classes.languageMenuLabel}>{translations('english')}</div>
            </div>
            <div className={classes.languageMenuItem} onClick={() => changeLanguage('de')}>
              <img className={classes.languageMenuFlag} src={deFlag} alt="deFlag" />
              <div className={classes.languageMenuLabel}>{translations('german')}</div>
            </div>
            <div className={classes.languageMenuItem} onClick={() => changeLanguage('fr')}>
              <img className={classes.languageMenuFlag} src={frFlag} alt="frFlag" />
              <div className={classes.languageMenuLabel}>{translations('french')}</div>
            </div>
            <div className={classes.languageMenuItem} onClick={() => changeLanguage('es')}>
              <img className={classes.languageMenuFlag} src={spFlag} alt="spFlag" />
              <div className={classes.languageMenuLabel}>{translations('spanish')}</div>
            </div>
            <div className={classes.languageMenuItem} onClick={() => changeLanguage('nl')}>
              <img className={classes.languageMenuFlag} src={nlFlag} alt="nlFlag" />
              <div className={classes.languageMenuLabel}>{translations('dutch')}</div>
            </div>
          </div>
        </DropDown>
        {loggedIn && <NotificationDropDown />}
        {loggedIn && (
          <div className={classes.userInfoContainer}>
            <div className={classes.userAvatar}>
              <Avatar variant="initals" initials={personInitials()} />
            </div>
            {/* <div className={classes.userNameContainer}>
              <div className={classes.userName}>{userRoleLabel}</div>
              <div className={classes.testCenterName}>{userLocation}</div>
            </div> */}
            <DropDown
              onClose={() => setOpenUserDDMenu(false)}
              isOpen={openUserDDMenu}
              Toggle={() => (
                <img
                  className={[
                    classes.userDropdownIcon,
                    ...(openUserDDMenu ? [classes.userDropdownIconRotated] : []),
                  ].join(' ')}
                  src={arrowDown}
                  alt={'arrow-down'}
                  onClick={() => setOpenUserDDMenu(!openUserDDMenu)}
                />
              )}
            >
              <div className={classes.userDDMenu}>
                <div className={classes.userDDMenuTop}>
                  <div
                    className={classes.userFullName}
                  >{`${personLoggedIn.person_firstname} ${personLoggedIn.person_lastname}`}</div>
                  <div className={classes.userName}>{userRoleLabel}</div>
                  <div className={classes.testCenterName}>{userLocation}</div>
                </div>
                <div className={classes.userDDMenuItem} onClick={() => handleLogout()}>
                  <div className={classes.languageMenuLabel}>{translations('logout')}</div>
                </div>
              </div>
            </DropDown>
            <div
              className={classes.toggleContainer}
              onClick={async () => {
                if (document.fullscreenElement) {
                  await document.exitFullscreen();
                  setIsFullScreen(null);
                } else {
                  await document.body.requestFullscreen();
                  setIsFullScreen(document.fullscreenElement);
                }
              }}
            >
              <img
                src={isFullScreen ? fullScreenOff : fullScreenOn}
                className={classes.fullScreenToggle}
                alt={'full-screen-on'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
